import React from "react";

import {
  AppsSection,
  FavoritesSection,
  HomeNavSection,
  MyWorkflowsSection,
  ModuleMenu,
  type ModuleMenuOption,
  type ModuleMenuSection,
} from "#app/components/navigation/module-menu";
import { useSidebarNav } from "#app/components/navigation/use-sidebar-nav";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { CreateTeamDialog } from "#app/components/teams/create-team-dialog";
import { CreateWorkflowDialog } from "#app/components/workflows/create-workflow-dialog";
import { useAppData } from "#app/lib/data";
import { useFavoriteIssues } from "#app/lib/hooks/use-favorite-issues";
import { useExternalAppLinks } from "#app/lib/hooks/use-external-app-links";
import { useCanInvite } from "#app/lib/hooks/use-can-invite";
import { usePermission } from "#app/lib/hooks/use-permission";
import { canEdit } from "#app/lib/utils/access-level";
import { useIssueView } from "#app/components/issue-view";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { useModuleRail } from "#app/components/navigation/module-rail/module-rail-context";
import { type HomeSectionId } from "./home-sections";

// id → Component for the user-orderable sections. Typed by `HomeSectionId`
// so adding a section to the registry without a component here is a type
// error (and vice versa the registry stays the single id source).
const SECTION_COMPONENTS: Record<HomeSectionId, React.ComponentType> = {
  favorites: FavoritesSection,
  "my-workflows": MyWorkflowsSection,
  apps: AppsSection,
};

export function HomeModuleMenu() {
  const { navigate } = useSidebarNav();
  const { workflows, refetch: refetchAppData } = useAppData();
  const { issues: favoriteIssues } = useFavoriteIssues();
  const externalAppLinks = useExternalAppLinks();
  const { openCreateIssue } = useIssueView();
  const canCreateWorkflow = usePermission("create.workflows");
  // Drives where we land after creating: the workflow settings editor is
  // gated on manage.edit_workflows, so a create-only user must go to the
  // board instead (mirrors the workflow landing page).
  const canEditWorkflows = usePermission("manage.edit_workflows");
  const canCreateTeam = usePermission("manage.teams");
  // "Create project" mirrors the board's create gate: the create.items role
  // AND edit-or-better access on at least one workflow the viewer can see.
  // A user with no workflow they can create into (no accessible workflows)
  // can't create a project anywhere, so the action is hidden.
  const canCreateItems = usePermission("create.items");
  const canCreateProject =
    canCreateItems && workflows.some((w) => canEdit(w.viewerAccessLevel));
  // Same grant-driven gate as the rail's Invite tile and the Members page.
  const canInvite = useCanInvite();
  const closeSidebar = useCloseSidebarIfMobile();
  const { homeSectionOrder } = useModuleRail();
  const [createWorkflowOpen, setCreateWorkflowOpen] = React.useState(false);
  const [createTeamOpen, setCreateTeamOpen] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(false);

  // The fixed nav section is always first; the rest follow the user's
  // configured order. A section is skipped while it has nothing to show —
  // the conditional applies AFTER ordering so it keeps its slot otherwise.
  // Favorites: skip when empty. My Workflows: skip when there are no visible
  // workflows AND the viewer can't create one (an empty, actionless accordion).
  const sections = React.useMemo<ModuleMenuSection[]>(() => {
    const list: ModuleMenuSection[] = [
      { id: "nav", Component: HomeNavSection },
    ];
    for (const id of homeSectionOrder) {
      if (id === "favorites" && favoriteIssues.length === 0) continue;
      if (
        id === "my-workflows" &&
        workflows.length === 0 &&
        !canCreateWorkflow
      )
        continue;
      // Apps: skip when the workspace has no external links (an empty,
      // actionless section otherwise renders a divider + blank header).
      if (id === "apps" && externalAppLinks.length === 0) continue;
      list.push({ id, Component: SECTION_COMPONENTS[id] });
    }
    return list;
  }, [homeSectionOrder, favoriteIssues.length, workflows.length, canCreateWorkflow, externalAppLinks.length]);

  // Home aggregates the create actions of every module, in this order:
  // project, workflow, team, invite (the last gated like the Invite tile).
  const options = React.useMemo<ModuleMenuOption[]>(
    () => [
      ...(canCreateProject
        ? [
            {
              id: "create-project",
              icon: "plus" as const,
              label: "Create project",
              onClick: () => {
                closeSidebar();
                openCreateIssue({ source: "home_menu" });
              },
            },
          ]
        : []),
      ...(canCreateWorkflow
        ? [
            {
              id: "create-workflow",
              icon: "workflow" as const,
              label: "Create workflow",
              onClick: () => {
                closeSidebar();
                setCreateWorkflowOpen(true);
              },
            },
          ]
        : []),
      ...(canCreateTeam
        ? [
            {
              id: "create-team",
              icon: "users" as const,
              label: "Create team",
              onClick: () => {
                closeSidebar();
                setCreateTeamOpen(true);
              },
            },
          ]
        : []),
      ...(canInvite
        ? [
            {
              id: "invite-member",
              icon: "user-plus" as const,
              label: "Invite member",
              onClick: () => {
                closeSidebar();
                setInviteOpen(true);
              },
            },
          ]
        : []),
    ],
    [
      canCreateProject,
      canCreateWorkflow,
      canCreateTeam,
      canInvite,
      closeSidebar,
      openCreateIssue,
    ],
  );

  return (
    <>
      <ModuleMenu
        title="Home"
        sections={sections}
        options={options}
        // Pinned footer opening Customize Navigation on the Home tab — the
        // module menu's own sections are what that tab reorders.
        customizeNav={{ initialTab: "home" }}
      />
      {canCreateWorkflow && (
        <CreateWorkflowDialog
          open={createWorkflowOpen}
          onOpenChange={setCreateWorkflowOpen}
          onCreated={async (workflowId) => {
            await refetchAppData();
            navigate(
              canEditWorkflows
                ? `/settings/workflows/${workflowId}`
                : `/workflow/${workflowId}`,
            );
          }}
        />
      )}
      {canCreateTeam && (
        <CreateTeamDialog
          open={createTeamOpen}
          onOpenChange={setCreateTeamOpen}
          onCreated={async (teamId) => {
            await refetchAppData();
            navigate(`/settings/teams/${teamId}`);
          }}
        />
      )}
      {canInvite && (
        <InviteMembersDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      )}
    </>
  );
}
